import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '@env/environment';

export interface DynamicConfig {
  client: string;
  wpurl: string;
  wpu: string;
  wpt: string;
  // Add other config properties as needed
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class DynamicConfigService {
  private config: DynamicConfig | null = null;
  private configSubject = new BehaviorSubject<DynamicConfig | null>(null);
  public config$ = this.configSubject.asObservable();
  private readonly CONFIG_STORAGE_KEY = 'studema_dynamic_config';
  private readonly CONFIG_EXPIRY_KEY = 'studema_config_expiry';
  private readonly CONFIG_EXPIRY_HOURS = 24; // Cache for 24 hours

  constructor(private http: HttpClient) {
    // Try to load from cache on service initialization
    this.loadFromCache();
  }

  /**
   * Load configuration from server or cache
   */
  async loadConfig(): Promise<DynamicConfig> {
    // Return cached config if still valid
    if (this.config && this.isConfigValid()) {
      return this.config;
    }

    // Try to load from localStorage first
    const cachedConfig = this.getCachedConfig();
    if (cachedConfig && this.isConfigValid()) {
      this.config = cachedConfig;
      this.configSubject.next(this.config);
      return this.config;
    }

    // Load from server
    try {
      // Try to build code from authenticated user info: st-<company-slug>-<company_id>
      const { companySlug, companyId } = this.getAuthCompanyMeta();
      const clientCode = environment.client;
      const codeParam = companySlug && companyId
        ? `st-${companySlug}-${companyId}`
        : `st-${clientCode}`;
      const url = `${environment.keyServer}?code=${codeParam}`;
      
      console.log('Loading dynamic config from:', url);
      
      const response = await this.http.get<any>(url).pipe(
        catchError(error => {
          console.error('Failed to load dynamic config:', error);
          return of(null);
        })
      ).toPromise();

      console.log('DynamicConfig: Raw response from server:', response);
      console.log('DynamicConfig: Response status:', response?.status);
      console.log('DynamicConfig: Response data:', response?.data);

      if (response && response.status === 'success' && response.data) {
        // Standard wrapped response: {status: "success", data: {...}}
        this.config = response.data;
        this.saveToCache(this.config);
        this.configSubject.next(this.config);
        console.log('Dynamic config loaded successfully (data format):', this.config);
        return this.config;
      } else if (response && response.status === 'success' && response.config) {
        // Config server format: {status: "success", config: {...}}
        this.config = response.config;
        this.saveToCache(this.config);
        this.configSubject.next(this.config);
        console.log('Dynamic config loaded successfully (config format):', this.config);
        return this.config;
      } else if (response && response.wpurl) {
        // Direct config response: {wpurl: "...", wpu: "...", wpt: "..."}
        this.config = response;
        this.saveToCache(this.config);
        this.configSubject.next(this.config);
        console.log('Dynamic config loaded successfully (direct format):', this.config);
        return this.config;
      } else {
        console.error('DynamicConfig: Invalid response structure. Expected either:');
        console.error('  - Data format: {status: "success", data: {wpurl: "...", wpu: "...", wpt: "..."}}');
        console.error('  - Config format: {status: "success", config: {wpurl: "...", wpu: "...", wpt: "..."}}');
        console.error('  - Direct: {wpurl: "...", wpu: "...", wpt: "..."}');
        console.error('DynamicConfig: Got:', response);
        throw new Error('Invalid configuration response');
      }
    } catch (error) {
      console.error('Failed to load dynamic config, using fallback:', error);
      return this.getFallbackConfig();
    }
  }

  /**
   * Extract company metadata from local storage auth payloads
   */
  private getAuthCompanyMeta(): { companySlug: string | null, companyId: string | null } {
    try {
      const possibleKeys = ['user', 'CURRENT_USER', 'auth', 'authData', 'app_user'];
      let payload: any = null;
      let foundKey: string | null = null;

      for (const key of possibleKeys) {
        const raw = localStorage.getItem(key);
        if (!raw) continue;
        try {
          const json = JSON.parse(raw);
          if (json) { 
            payload = json; 
            foundKey = key;
            break; 
          }
        } catch { /* ignore parse errors */ }
      }

      console.log('DynamicConfig: Found auth payload in key:', foundKey);
      console.log('DynamicConfig: Raw payload:', payload);

      if (!payload) {
        console.log('DynamicConfig: No payload found, returning null');
        return { companySlug: null, companyId: null };
      }

      // Some apps store under { data: {...} }
      const data = payload.data ?? payload;
      console.log('DynamicConfig: Extracted data object:', data);

      // Also check nested user object (your auth structure has user.company_id and user.company)
      const user = data.user ?? {};
      console.log('DynamicConfig: Extracted user object:', user);

      const companyName: string | undefined = 
          data.company ?? data.companyname ?? data.company_name ?? 
          user.company ?? user.companyname ?? user.company_name;
          
      const companyId: string | number | undefined = 
          data.company_id ?? data.companyId ?? data.companyid ?? 
          user.company_id ?? user.companyId ?? user.companyid;

      console.log('DynamicConfig: Extracted companyName:', companyName);
      console.log('DynamicConfig: Extracted companyId:', companyId);

      if (!companyName || !companyId) {
        console.log('DynamicConfig: Missing companyName or companyId, returning null');
        return { companySlug: null, companyId: null };
      }

      const slug = this.sanitizeCompanyName(String(companyName));
      console.log('DynamicConfig: Generated slug from companyName:', slug);
      console.log('DynamicConfig: Final result:', { companySlug: slug, companyId: String(companyId) });
      
      return { companySlug: slug, companyId: String(companyId) };
    } catch (error) {
      console.log('DynamicConfig: Error in getAuthCompanyMeta:', error);
      return { companySlug: null, companyId: null };
    }
  }

  /**
   * Sanitize company name to URL-safe slug (remove diacritics, non-alnum to '-')
   */
  private sanitizeCompanyName(name: string): string {
    return name
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // strip accents
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/-+/g, '-');
  }

  /**
   * Get WordPress configuration
   */
  async getWordpressConfig(): Promise<{wpurl: string, wpu: string, wpt: string}> {
    const config = await this.loadConfig();
    return {
      wpurl: config.wpurl,
      wpu: config.wpu,
      wpt: config.wpt
    };
  }

  /**
   * Get specific configuration value
   */
  async getConfigValue(key: string): Promise<any> {
    const config = await this.loadConfig();
    return config[key];
  }

  /**
   * Check if current config is still valid (not expired)
   */
  private isConfigValid(): boolean {
    const expiry = localStorage.getItem(this.CONFIG_EXPIRY_KEY);
    if (!expiry) return false;
    
    const expiryTime = parseInt(expiry);
    const now = Date.now();
    return now < expiryTime;
  }

  /**
   * Get cached config from localStorage
   */
  private getCachedConfig(): DynamicConfig | null {
    try {
      const cached = localStorage.getItem(this.CONFIG_STORAGE_KEY);
      return cached ? JSON.parse(cached) : null;
    } catch (error) {
      console.error('Failed to parse cached config:', error);
      return null;
    }
  }

  /**
   * Save config to localStorage with expiry
   */
  private saveToCache(config: DynamicConfig): void {
    try {
      localStorage.setItem(this.CONFIG_STORAGE_KEY, JSON.stringify(config));
      const expiryTime = Date.now() + (this.CONFIG_EXPIRY_HOURS * 60 * 60 * 1000);
      localStorage.setItem(this.CONFIG_EXPIRY_KEY, expiryTime.toString());
    } catch (error) {
      console.error('Failed to save config to cache:', error);
    }
  }

  /**
   * Load config from cache on service initialization
   */
  private loadFromCache(): void {
    const cachedConfig = this.getCachedConfig();
    if (cachedConfig && this.isConfigValid()) {
      this.config = cachedConfig;
      this.configSubject.next(this.config);
    }
  }

  /**
   * Get fallback configuration from environment
   */
  private getFallbackConfig(): DynamicConfig {
    console.log('Using fallback configuration from environment');
    return {
      client: environment.client,
      wpurl:  environment.fallback_wpurl ,
      wpu: '',
      wpt: ''
    };
  }

  /**
   * Clear cached configuration
   */
  clearCache(): void {
    localStorage.removeItem(this.CONFIG_STORAGE_KEY);
    localStorage.removeItem(this.CONFIG_EXPIRY_KEY);
    this.config = null;
    this.configSubject.next(null);
  }

  /**
   * Force reload configuration from server
   */
  async reloadConfig(): Promise<DynamicConfig> {
    this.clearCache();
    return this.loadConfig();
  }
}
